/**
 * ErrorPanel — friendly headline + body + collapsible "Technical details".
 *
 * Plan §"Error handling & triage architecture" Layer 3 — used by Catalog,
 * Active list, HITL drawer, failed-phase view. Surfaces every diagnostic
 * field from `ApiError` (correlation_id, auth_correlation_id, status,
 * url, method, response_body, timestamp, auth_tier) plus a copy-to-
 * clipboard button on the JSON.
 *
 * Accepts either an `ApiError` instance or a plain object with the same
 * shape — tests don't have to fabricate a real ApiError.
 */
import { useCallback, useState } from "react";
import { ApiError } from "../services/orchestratorApi";
import type { ApiErrorPayload } from "../services/orchestratorApi";
import { toUserMessage } from "../utils/userErrorMessage";

export interface ErrorPanelProps {
  error: ApiError | ApiErrorPayload | Error | unknown;
  /** Headline — defaults to a friendly framing. */
  title?: string;
  /** Optional caller-controlled body line above the technical details. */
  description?: string;
  /** Optional "Try again" handler. When provided, renders a retry button. */
  onRetry?: () => void;
  /** Compact variant — used inside the HITL drawer where vertical room is tight. */
  compact?: boolean;
}

interface NormalizedError {
  message: string;
  correlation_id: string | null;
  auth_correlation_id: string | null;
  auth_tier: string | null;
  status: number | null;
  url: string | null;
  method: string | null;
  response_body: unknown;
  request_body: unknown;
  timestamp: string;
}

function normalize(err: unknown): NormalizedError {
  const fallbackTimestamp = new Date().toISOString();
  if (err instanceof ApiError) {
    return {
      message: err.message,
      correlation_id: err.correlation_id ?? null,
      // ApiError doesn't carry a separate auth_correlation_id field
      // today; surface auth_tier and reuse correlation_id as the
      // primary support handle. The "auth_correlation_id" hook is
      // present so the support tier in the plan can populate it once
      // the auth layer emits one independently of the api correlation.
      auth_correlation_id: null,
      auth_tier: err.auth_tier ?? null,
      status: err.status ?? null,
      url: err.url ?? null,
      method: err.method ?? null,
      response_body: err.response_body,
      request_body: err.request_body,
      timestamp: err.timestamp ?? fallbackTimestamp,
    };
  }
  if (err && typeof err === "object") {
    const o = err as Record<string, unknown>;
    return {
      message: typeof o.message === "string" ? o.message : "Something went wrong",
      correlation_id:
        typeof o.correlation_id === "string"
          ? o.correlation_id
          : typeof o.correlationId === "string"
            ? (o.correlationId as string)
            : null,
      auth_correlation_id:
        typeof o.auth_correlation_id === "string"
          ? (o.auth_correlation_id as string)
          : null,
      auth_tier: typeof o.auth_tier === "string" ? (o.auth_tier as string) : null,
      status: typeof o.status === "number" ? (o.status as number) : null,
      url: typeof o.url === "string" ? (o.url as string) : null,
      method: typeof o.method === "string" ? (o.method as string) : null,
      response_body: o.response_body,
      request_body: o.request_body,
      timestamp:
        typeof o.timestamp === "string" ? (o.timestamp as string) : fallbackTimestamp,
    };
  }
  return {
    message: String(err ?? "Something went wrong"),
    correlation_id: null,
    auth_correlation_id: null,
    auth_tier: null,
    status: null,
    url: null,
    method: null,
    response_body: null,
    request_body: null,
    timestamp: fallbackTimestamp,
  };
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export default function ErrorPanel({
  error,
  title = "Something went wrong",
  description,
  onRetry,
  compact = false,
}: ErrorPanelProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const norm = normalize(error);

  const detailsJson = safeStringify({
    message: norm.message,
    correlation_id: norm.correlation_id,
    auth_correlation_id: norm.auth_correlation_id,
    auth_tier: norm.auth_tier,
    status: norm.status,
    url: norm.url,
    method: norm.method,
    response_body: norm.response_body,
    request_body: norm.request_body,
    timestamp: norm.timestamp,
  });

  const handleCopy = useCallback(async () => {
    try {
      const clip = (navigator as { clipboard?: { writeText: (s: string) => Promise<void> } })
        .clipboard;
      if (clip?.writeText) {
        await clip.writeText(detailsJson);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // clipboard write blocked — leave the JSON visible in the panel
    }
  }, [detailsJson]);

  const padding = compact ? "p-3" : "p-4";

  return (
    <div
      role="alert"
      className={`tile-shadow rounded-lg bg-primary-error-25 border border-primary-error-200 ${padding} flex flex-col gap-2`}
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-bold text-primary-error-700">{title}</h3>
        <p className="text-sm text-primary-gray-light-700">
          {description ?? toUserMessage(norm.message)}
        </p>
      </div>

      <div className="flex flex-row flex-wrap items-center gap-3 text-xs text-primary-gray-light-600">
        {norm.correlation_id && (
          <span>
            ID:{" "}
            <code className="font-mono text-primary-gray-light-700">
              {norm.correlation_id}
            </code>
          </span>
        )}
        {norm.auth_tier && (
          <span>
            via:{" "}
            <code className="font-mono text-primary-gray-light-700">
              {norm.auth_tier}
            </code>
          </span>
        )}
        {norm.status !== null && (
          <span>
            status:{" "}
            <code className="font-mono text-primary-gray-light-700">
              {norm.status}
            </code>
          </span>
        )}
      </div>

      <div className="flex flex-row items-center gap-2">
        {import.meta.env.DEV && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="btn-tertiary-gray text-xs px-2 py-1"
            aria-expanded={open}
            aria-controls="error-panel-details"
          >
            {open ? "Hide details" : "Show details"}
          </button>
        )}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="btn-secondary-gray text-xs px-2 py-1"
          >
            Try again
          </button>
        )}
      </div>

      {import.meta.env.DEV && (
        <div
          id="error-panel-details"
          className={`overflow-hidden motion-safe:transition-[max-height] motion-safe:duration-200 ${
            open ? "max-h-96" : "max-h-0"
          }`}
        >
          <div className="flex flex-col gap-2 pt-2">
            <button
              type="button"
              onClick={handleCopy}
              className="btn-secondary-gray text-xs px-2 py-1 self-start"
            >
              {copied ? "Copied!" : "Copy details"}
            </button>
            <pre className="text-2xs font-mono bg-primary-gray-light-50 border border-primary-gray-light-200 rounded p-2 overflow-auto whitespace-pre-wrap break-all">
              {detailsJson}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
